import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';

// Flatten the nested locale bundle into i18next namespaces.
// The merged JSON has { ui: {...}, checks: { checkFolder: {...}, ... } }
// We create namespaces: "ui" and one per check folder (e.g. "checks.fileType").
const resources: Record<string, Record<string, Record<string, string>>> = {
  en: {
    ui: en.ui,
  },
};

for (const [folder, strings] of Object.entries(en.checks)) {
  resources.en[`checks.${folder}`] = strings;
}

i18n.use(initReactI18next).init({
  resources,
  lng: 'en',
  fallbackLng: 'en',
  fallbackNS: 'ui',
  interpolation: {
    escapeValue: false, // React already escapes
  },
  defaultNS: 'ui',
});

/**
 * Map from check IDs (snake_case, used in API responses) to folder names
 * (camelCase, used as i18next namespace keys).
 */
const CHECK_ID_TO_FOLDER: Record<string, string> = {
  file_type: 'fileType',
  first_payment_date: 'firstPaymentDate',
  debt_vs_plan_match: 'debtVsPlanMatch',
  company_duplicate: 'companyDuplicate',
  enrolled_duplicate: 'enrolledDuplicate',
  is_co_applicant: 'isCoApplicant',
  email_validation: 'emailValidation',
  client_info_accuracy: 'clientInfoAccuracy',
  min_debt_threshold: 'minDebtThreshold',
  min_monthly_payment: 'minMonthlyPayment',
  prohibited_states: 'prohibitedStates',
  age_limit: 'ageLimit',
  credit_report_present: 'creditReportPresent',
  debt_completeness: 'debtCompleteness',
  program_length: 'programLength',
  payment_method_present: 'paymentMethodPresent',
  ssn_valid: 'ssnValid',
  enrollment_plan_present: 'enrollmentPlanPresent',
};

/**
 * Map from error codes to remediation string keys within the check's namespace.
 */
const ERROR_CODE_TO_REMEDIATION_KEY: Record<string, string> = {
  'file_type.wrong_type': 'remediation.wrongType',
  'first_payment_date.exceeds_limit': 'remediation.exceedsLimit',
  'first_payment_date.no_plan': 'remediation.noPlan',
  'first_payment_date.no_payments': 'remediation.noPayments',
  'debt_vs_plan.api_unavailable': 'remediation.apiUnavailable',
  'debt_vs_plan.amount_mismatch': 'remediation.amountMismatch',
  'debt_vs_plan.csa_unparseable': 'remediation.csaUnparseable',
  'debt_vs_plan.csa_unavailable': 'remediation.csaUnavailable',
  'debt_vs_plan.stale_contact_total': 'remediation.staleContactTotal',
  'debt_vs_plan.no_debts': 'remediation.noDebts',
  'debt_vs_plan.no_csa': 'remediation.noCsa',
  'debt_vs_plan.invalid_total': 'remediation.invalidTotal',
  'min_debt_threshold.no_debts': 'remediation.noDebts',
  'company_duplicate.existing': 'remediation.existing',
  'enrolled_duplicate.existing': 'remediation.existing',
  'enrolled_duplicate.canceled': 'remediation.canceled',
  'enrolled_duplicate.coapp_existing': 'remediation.coapp_existing',
  'is_co_applicant.is_co_applicant': 'remediation.is_co_applicant',
  'email.missing': 'remediation.email.missing',
  'email.invalid_format': 'remediation.email.invalid_format',
  'client_info.field_mismatch': 'remediation.fieldMismatch',
  'client_info.no_credit_report': 'remediation.noCreditReport',
  'client_info.no_report': 'remediation.noReport',
  'first_payment_date.too_soon': 'remediation.tooSoon',
  'min_debt_threshold.api_unavailable': 'remediation.apiUnavailable',
  'min_debt_threshold.below_minimum': 'remediation.belowMinimum',
  'min_debt_threshold.invalid_amount': 'remediation.invalidAmount',
  'min_monthly_payment.below_minimum': 'remediation.belowMinimum',
  'min_monthly_payment.no_plan': 'remediation.noPlan',
  'prohibited_states.prohibited': 'remediation.prohibited',
  'prohibited_states.no_state': 'remediation.no_state',
  'age_limit.exceeds_max': 'remediation.exceedsMax',
  'age_limit.no_dob': 'remediation.noDob',
  'credit_report_present.missing': 'remediation.missing',
  'credit_report_present.api_unavailable': 'remediation.apiUnavailable',
  'debt_completeness.api_unavailable': 'remediation.apiUnavailable',
  'debt_completeness.missing_fields': 'remediation.missingFields',
  'debt_completeness.no_debts': 'remediation.noDebts',
  'program_length.non_standard': 'remediation.nonStandard',
  'program_length.no_plan': 'remediation.noPlan',
  'payment_method_present.no_payment_method': 'remediation.noPaymentMethod',
  'payment_method_present.api_error': 'remediation.apiError',
  'ssn_valid.missing': 'remediation.missing',
  'ssn_valid.invalid': 'remediation.invalid',
  'enrollment_plan_present.api_unavailable': 'remediation.apiUnavailable',
  'enrollment_plan_present.no_plan': 'remediation.noPlan',
  'enrollment_plan_present.wrong_program': 'remediation.wrongProgram',
};

/** Get the i18next namespace for a check by its checkId. */
export function getCheckNamespace(checkId: string): string {
  const folder = CHECK_ID_TO_FOLDER[checkId] ?? checkId;
  return `checks.${folder}`;
}

/** Get the remediation key for a given error code. */
export function getRemediationKey(errorCode: string): string | undefined {
  return ERROR_CODE_TO_REMEDIATION_KEY[errorCode];
}

export default i18n;
